import { useMemo, useState, type ReactNode } from 'react';
import { CheckCheck } from 'lucide-react';
import { Input, PageLoader, SegmentedToggle, TextButton, Typography } from '../ui';
import { useContactBook, type WebContact } from '../../../lib/contact-book';
import { isNewContact, matchesChip, sectionsOf, type ContactChip, type ContactRowEntry } from '../../../lib/contact-rows';
import { useContactSeen } from '../../../lib/use-contact-seen';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useTranslation } from '../../../lib/i18n';
import { ContactRow } from './ContactRow';
import styles from './contacts.module.css';

const CHIPS: ContactChip[] = ['all', 'new', 'action-required'];

/**
 * The contacts list.
 *
 * **Written-down exception — pull-to-refresh is native-only here, and that is an idiom difference
 * rather than a gap.** Both phones' contacts screens carry it, matching every other native list
 * (Routines, Agent Tasks), because their cache is a plain store dictionary with NO staleness: it is
 * held until sign-out, so without the gesture there is no way to ask for fresh rows. Web's cache is
 * React Query's, with `staleTime: 30_000`, so a revisit past thirty seconds refetches on its own —
 * and a browser already offers reload, including its own pull gesture on mobile web. No screen in
 * this app has a bespoke refresh control, and adding one only here would be the odd one out.
 *
 * Search goes to the server because it is a different question — it resolves against the whole
 * people book and finds people with no outreach at all, which the browse deliberately does not
 * return. The chips do NOT: every entry already carries `waitingOnMe` and `needsAttention`, so
 * filtering here is exact, while a server-side chip could only trim the page it had fetched.
 */
export function ContactsBody({
  header,
  onOpen,
}: {
  header?: ReactNode;
  onOpen: (personId: string) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState<ContactChip>('all');
  const state = useContactBook(search.trim());
  const seen = useContactSeen();

  // Every line is a person, and the LAST communication with them is the order.
  const entries = useMemo(() => {
    const list = state.kind === 'ready' ? [...state.contacts] : [];
    const at = (e: WebContact) => (e.lastActivityAt ? new Date(e.lastActivityAt).getTime() || 0 : 0);
    return list.sort((a, b) => at(b) - at(a));
  }, [state]);
  const now = Date.now();
  const isNew = (entry: ContactRowEntry) => isNewContact(entry, seen.seenAt(entry.contact?.personId), now);
  const newIds = useMemo(
    () => entries.filter(isNew).map((e) => e.contact?.personId).filter((id): id is string => !!id),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `seen.generation` is the mark's clock
    [entries, seen.generation, seen.ready],
  );
  const filtered = useMemo(
    () => entries.filter((entry) => matchesChip(entry, chip, isNew)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `seen.generation` is the mark's clock
    [entries, chip, seen.generation, seen.ready],
  );
  const { needsAttention, all } = useMemo(() => sectionsOf(filtered), [filtered]);
  const markAllRead = () => {
    logPageClickBi({ page_name: 'contacts_page', button_name: 'mark_all_read' });
    seen.markAll(newIds);
  };

  return (
    <div className={styles.body}>
      {header}

      <div className={styles.search}>
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('contacts.search.placeholder')}
        />
      </div>

      {/* The DS segmented toggle is the app's pick-one-of-a-few row; a hand-rolled row of pressable
          chips is what `button-spec` and `segmented-toggle-spec` exist to prevent. */}
      <div className={styles.chips}>
        <SegmentedToggle<ContactChip>
          ariaLabel={t('contacts.title')}
          options={CHIPS.map((value) => ({ value, label: t(`contacts.chip.${value}`) }))}
          value={chip}
          onChange={setChip}
          size="small"
        />
        {newIds.length > 0 ? (
          <TextButton size="small" className={styles.markAll} startIcon={<CheckCheck size={16} aria-hidden />} onClick={markAllRead}>
            {t('contacts.markAllRead')}
          </TextButton>
        ) : null}
      </div>

      {state.kind === 'loading' ? <PageLoader /> : null}

      {state.kind === 'error' ? (
        <div className={styles.empty}>
          <Typography variant="p2">{t('contacts.error.title')}</Typography>
          <Typography variant="p3" color="secondary">
            {state.message}
          </Typography>
        </div>
      ) : null}

      {state.kind === 'ready' && filtered.length === 0 ? (
        <div className={styles.empty}>
          <Typography variant="p2">
            {search ? t('contacts.empty.search.title') : t('contacts.empty.title')}
          </Typography>
          <Typography variant="p3" color="secondary">
            {search ? t('contacts.empty.search.body') : t('contacts.empty.body')}
          </Typography>
        </div>
      ) : null}

      {needsAttention.length > 0 ? (
        <>
          <Typography variant="p3" color="secondary" className={styles.sectionLabel}>
            {`${t('contacts.chip.action-required')} · ${needsAttention.length}`}
          </Typography>
          <div className={styles.list}>
            {needsAttention.map((entry) => (
              <ContactRow key={rowKey(entry)} entry={entry} onOpen={onOpen} isNew={isNew(entry)} />
            ))}
          </div>
        </>
      ) : null}

      {all.length > 0 ? (
        <>
          <Typography variant="p3" color="secondary" className={styles.sectionLabel}>
            {t('contacts.section.all')}
          </Typography>
          <div className={styles.list}>
            {all.map((entry) => (
              <ContactRow key={rowKey(entry)} entry={entry} onOpen={onOpen} isNew={isNew(entry)} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * A stable key.
 *
 * Falls back to the newest outreach id for a counterparty with no Person behind them — those rows
 * have no person id at all, and keying every one of them on "" would make React treat two different
 * strangers as the same row.
 */
function rowKey(entry: { contact?: { personId?: string }; outbound?: { outreachId: string }[]; inbound?: { outreachId: string }[] }): string {
  return (
    entry.contact?.personId ||
    entry.outbound?.[0]?.outreachId ||
    entry.inbound?.[0]?.outreachId ||
    ''
  );
}
